import { useState, useMemo } from "react";
import { Plus, Play, UserCheck, Check } from "lucide-react";
import { Button } from "../common";
import PageHeader from "../common/PageHeader";
import toast from "react-hot-toast";
import { WorkflowCard, WorkflowModule } from "./components/workflow/WorkflowCard";
import WorkflowPipeline, {
  type PipelineNode,
} from "./components/workflow/WorkflowPipeline";
import { CreateWorkflowDialog } from "./components/workflow/CreateWorkflowDialog";
import { EditWorkflowDialog } from "./components/workflow/EditWorkflowDialog";
import { WorkflowSkeleton } from "./components/workflow/WorkflowSkeleton";
import {
  useGetWorkflowDefinitionsQuery,
  useCreateWorkflowDefinitionMutation,
  useUpdateWorkflowDefinitionMutation,
  IWorkflowDefinition,
  useDeleteWorkflowDefinitionMutation,
} from "../../store/apis/dynamicWorkflow.api";
import { capitalizeWords } from "../../utils/nameUtils";
import { getLabelForModules } from "../../utils/dynamicworkflow/workflowUtils";

interface WorkflowLevelPayload {
  groupId: string;
  sequence: number;
  canBeSkipped: boolean;
}

const DynamicWorkflow = () => {
  const { data: workflowsResponse, isLoading } = useGetWorkflowDefinitionsQuery();

  const [createWorkflow, { isLoading: isCreating }] =
    useCreateWorkflowDefinitionMutation();
  const [updateWorkflow, { isLoading: isUpdating }] =
    useUpdateWorkflowDefinitionMutation();
  const [deleteWorkflow, { isLoading: isDeleting }] =
    useDeleteWorkflowDefinitionMutation();

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] =
    useState<IWorkflowDefinition | null>(null);

  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null);

  const workflows = workflowsResponse?.data?.definitions || [];

  const selectedWorkflow = useMemo(
    () => workflows.find((w) => w.id === selectedWorkflowId) || null,
    [workflows, selectedWorkflowId]
  );

  const pipelineNodes: PipelineNode[] = useMemo(() => {
    if (!selectedWorkflow) return [];
    const nodes: PipelineNode[] = [
      { label: "Request Submitted", sub: "Trigger", state: "start", icon: Play },
    ];
    (selectedWorkflow.steps || [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence)
      .forEach((step) => {
        nodes.push({
          label: `Level ${step.sequence}`,
          sub: step.canBeSkipped ? "Optional" : "Required",
          state: "mid",
          icon: UserCheck,
        });
      });
    nodes.push({ label: "Approved & Notified", sub: "End", state: "end", icon: Check });
    return nodes;
  }, [selectedWorkflow]);

  const handleSave = async (data: {
    name: string;
    description: string;
    permissionCode: string;
    isActive?: boolean;
    canAdminApprove: boolean;
    levels: WorkflowLevelPayload[];
  }) => {
    try {
      await createWorkflow({
        name: data.name,
        description: data.description,
        permissionCode: data.permissionCode,
        isActive: data.isActive ?? false,
        canAdminApproveMidFlow: data.canAdminApprove,
        steps: data.levels.map((level) => ({
          groupId: level.groupId,
          sequence: level.sequence,
          canBeSkipped: level.canBeSkipped,
        })),
      }).unwrap();
      setCreateOpen(false);
      toast.success(`"${data.name}" has been created successfully.`);
    } catch (error) {
      toast.error("Failed to create workflow. Please try again.");
      console.error(error);
    }
  };

  const handleEdit = (workflow: IWorkflowDefinition) => {
    setEditingWorkflow(workflow);
    setEditOpen(true);
  };

  const handleEditSave = async (data: {
    module: string;
    name: string;
    description: string;
    levels: WorkflowLevelPayload[];
    canAdminApprove: boolean;
    isActive: boolean;
  }) => {
    if (!editingWorkflow?.id) return;
    try {
      await updateWorkflow({
        id: editingWorkflow.id,
        data: {
          name: data.name,
          description: data.description,
          canAdminApproveMidFlow: data.canAdminApprove,
          isActive: data.isActive,
          steps: data.levels.map((level) => ({
            groupId: level.groupId,
            sequence: level.sequence,
            canBeSkipped: level.canBeSkipped,
          })),
        },
      }).unwrap();
      setEditOpen(false);
      setEditingWorkflow(null);
      toast.success(`"${data.name}" has been updated.`);
    } catch (error) {
      toast.error("Failed to update workflow. Please try again.");
      console.error(error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteWorkflow({ id, data: { isActive: false } }).unwrap();
      toast.success("Workflow deleted successfully.");
    } catch (error: any) {
      toast.error(error.data.message || "Failed to delete workflow.");
      console.error(error);
    }
  };

  const formatForUI = (apiWorkflow: IWorkflowDefinition): WorkflowModule => ({
    id: apiWorkflow.id ?? "",
    name: capitalizeWords(apiWorkflow.name),
    description: apiWorkflow.description || "",
    module: apiWorkflow.permissionCode || "",
    isActive: apiWorkflow.isActive,
    levels: [
      { label: "E", type: "employee" as const },
      ...(apiWorkflow.steps || []).map((step) => ({
        label: `L${step.sequence}`,
        type: "level" as const,
      })),
    ],
    createdAt: apiWorkflow.createdAt || "Recently",
  });

  return (
    <div className="max-w-full mx-auto">
      <div className="space-y-6">
        <PageHeader
          title="Dynamic Workflows"
          description="Approval routing and escalation rules"
          breadcrumbs={[{ label: "Home" }, { label: "Workflows" }]}
          actions={
            <Button
              appearance="primary"
              onClick={() => setCreateOpen(true)}
              icon={<Plus className="w-4 h-4" />}
            >
              New Workflow
            </Button>
          }
        />

        {/* Dynamic Workflow Pipeline */}
        <div>
          {selectedWorkflow ? (
            <WorkflowPipeline nodes={pipelineNodes} />
          ) : (
            <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-secondary)] rounded-[18px] p-8 text-center text-[var(--color-text-tertiary)] text-[13px]">
              Select a workflow card to view its pipeline
            </div>
          )}
        </div>


        {/* Workflow cards — 2-col grid */}
        <div>
          <h2 className="text-[15px] font-extrabold text-[var(--color-text-primary)] mb-3">
            Workflow Definitions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {isLoading ? (
              <div className="col-span-2">
                <WorkflowSkeleton count={2} />
              </div>
            ) : workflows.length === 0 ? (
              <div className="col-span-2 text-center py-12 text-[var(--color-text-tertiary)]">
                <p>No workflows found.</p>
              </div>
            ) : (
              workflows.map((apiWorkflow) => (
                <WorkflowCard
                  key={apiWorkflow.id}
                  workflow={formatForUI(apiWorkflow)}
                  onEdit={() => handleEdit(apiWorkflow)}
                  onDelete={() => handleDelete(apiWorkflow.id ?? "")}
                  isDeleting={isDeleting}
                  onClick={() => setSelectedWorkflowId(apiWorkflow.id ?? "")}
                  isSelected={selectedWorkflowId === apiWorkflow.id}
                />
              ))
            )}
          </div>
        </div>

        <CreateWorkflowDialog
          open={createOpen}
          onOpenChange={setCreateOpen}
          onSave={handleSave}
          isCreating={isCreating}
        />

        {editingWorkflow && (
          <EditWorkflowDialog
            open={editOpen}
            onOpenChange={(isOpen) => {
              setEditOpen(isOpen);
              if (!isOpen) setEditingWorkflow(null);
            }}
            workflow={editingWorkflow}
            onSave={handleEditSave}
            isUpdating={isUpdating}
          />
        )}
      </div>
    </div>
  );
};

export default DynamicWorkflow;
